/**
 * Company Agent Management — v2.0
 *
 * Read + edit screen for the Company Admin to manage agents that source leads
 * to the company. Mirrors CompanyUserManagement's outer skeleton verbatim;
 * impersonation flow intentionally absent (agents are external referrers).
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { classNames } from 'primereact/utils';
import companyAgentService from '../../services/companyAgentService';

const STATUS_OPTIONS = [
    { label: 'All statuses', value: null },
    { label: 'Active',       value: 'active' },
    { label: 'Disabled',     value: 'disabled' },
];

const ACTIVE_OPTIONS = [
    { label: 'Active',   value: true },
    { label: 'Disabled', value: false },
];

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const CompanyAgentManagement = () => {
    const toast = useRef(null);

    const canAccess = companyAgentService.isCompanyAdmin();

    const companyName = (() => {
        try { return JSON.parse(localStorage.getItem('companyInfo') || '{}')?.name || null; }
        catch { return null; }
    })();

    // ── list state ────────────────────────────────────────────────────────
    const [loading, setLoading]                 = useState(false);
    const [agents, setAgents]                   = useState([]);
    const [searchQuery, setSearchQuery]         = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter]       = useState(null);

    const [first, setFirst]               = useState(0);
    const [rows, setRows]                 = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField]       = useState('name');
    const [sortOrder, setSortOrder]       = useState(1);

    // ── view dialog ───────────────────────────────────────────────────────
    const [viewDialogVisible, setViewDialogVisible] = useState(false);
    const [viewAgent, setViewAgent]                 = useState(null);
    const [viewLoading, setViewLoading]             = useState(false);

    // ── edit dialog ───────────────────────────────────────────────────────
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [editAgent, setEditAgent]                 = useState(null);
    const [editForm, setEditForm]                   = useState({
        first_name: '', last_name: '', email: '', mobile_number: '', is_active: true,
    });
    const [editErrors, setEditErrors] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);

    // ── debounce search ───────────────────────────────────────────────────
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setFirst(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // ── fetch agents on filter / pagination change ────────────────────────
    const loadAgents = useCallback(async () => {
        if (!canAccess) return;
        setLoading(true);
        try {
            const result = await companyAgentService.getAgents({
                page:       Math.floor(first / rows) + 1,
                per_page:   rows,
                search:     debouncedSearch,
                sort_field: sortField,
                sort_order: sortOrder === 1 ? 'asc' : 'desc',
                status:     statusFilter,
            });
            if (result.success) {
                setAgents(result.agents);
                setTotalRecords(result.pagination?.total ?? 0);
            } else {
                toast.current?.show({
                    severity: 'error', summary: 'Load failed',
                    detail: result.message, life: 3000,
                });
            }
        } finally {
            setLoading(false);
        }
    }, [canAccess, first, rows, debouncedSearch, sortField, sortOrder, statusFilter]);

    useEffect(() => { loadAgents(); }, [loadAgents]);

    // ── handlers ──────────────────────────────────────────────────────────
    const onPage = (event) => { setFirst(event.first); setRows(event.rows); };
    const onSort = (event) => { setSortField(event.sortField); setSortOrder(event.sortOrder); };

    const clearFilters = () => {
        setSearchQuery('');
        setStatusFilter(null);
        setFirst(0);
    };

    const openView = async (agent) => {
        setViewAgent(agent);
        setViewDialogVisible(true);
        setViewLoading(true);
        const result = await companyAgentService.getAgent(agent.id);
        if (result.success) {
            setViewAgent(result.agent);
        } else {
            toast.current?.show({
                severity: 'error', summary: 'Load failed',
                detail: result.message, life: 3000,
            });
        }
        setViewLoading(false);
    };

    const openEdit = (agent) => {
        setEditAgent(agent);
        setEditForm({
            first_name:    agent.first_name || '',
            last_name:     agent.last_name || '',
            email:         agent.email || '',
            mobile_number: agent.mobile_number || '',
            is_active:     !!agent.is_active,
        });
        setEditErrors({});
        setEditDialogVisible(true);
    };

    const validateEdit = () => {
        const errors = {};
        if (!editForm.first_name.trim()) {
            errors.first_name = 'Please enter the agent\'s first name.';
        }
        if (!editForm.last_name.trim()) {
            errors.last_name = 'Please enter the agent\'s last name.';
        }
        if (!editForm.email.trim()) {
            errors.email = 'Please enter an email address.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
            errors.email = 'Please enter a valid email address.';
        }
        if (!editForm.mobile_number) {
            errors.mobile_number = 'Please enter a mobile number.';
        } else if (!/^[6-9]\d{9}$/.test(editForm.mobile_number)) {
            errors.mobile_number = 'Enter a valid 10-digit Indian mobile number.';
        }
        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const submitEdit = async () => {
        if (!validateEdit()) return;
        setSavingEdit(true);
        const result = await companyAgentService.updateAgent(editAgent.id, editForm);
        setSavingEdit(false);

        if (result.success) {
            toast.current?.show({
                severity: 'success', summary: 'Saved',
                detail: 'Agent details updated.', life: 2500,
            });
            setEditDialogVisible(false);
            loadAgents();
        } else {
            const fieldErrors = result.errors || {};
            const mapped = {};
            ['first_name', 'last_name', 'email', 'mobile_number'].forEach((k) => {
                if (fieldErrors[k]?.[0]) mapped[k] = fieldErrors[k][0];
            });
            setEditErrors(mapped);
            toast.current?.show({
                severity: 'error', summary: 'Save failed',
                detail: result.message || 'Could not update agent.', life: 3500,
            });
        }
    };

    // ── access denied ─────────────────────────────────────────────────────
    if (!canAccess) {
        return (
            <>
                <Toast ref={toast} position="top-right" />
                <div className="grid">
                    <div className="col-12">
                        <Card>
                            <div className="flex flex-column align-items-center py-6">
                                <i className="pi pi-lock pi-4x text-400 mb-3" aria-hidden="true" />
                                <h1 className="text-xl font-semibold text-900 m-0 mb-2">Access Denied</h1>
                                <p className="text-600 text-sm m-0">Only company admins can manage agents.</p>
                            </div>
                        </Card>
                    </div>
                </div>
            </>
        );
    }

    // ── column body templates ─────────────────────────────────────────────
    const nameBody = (row) => (
        <div className="flex flex-column">
            <span className="text-sm font-medium text-900">{row.name}</span>
            {(row.first_name || row.last_name) && (
                <small className="text-500">
                    {[row.first_name, row.last_name].filter(Boolean).join(' ')}
                </small>
            )}
        </div>
    );

    const emailBody = (row) => (
        <span className="text-sm text-700">{row.email || '—'}</span>
    );

    const mobileBody = (row) => (
        <span className={`text-sm ${row.mobile_number ? 'text-700' : 'text-500'}`}>
            {row.mobile_number || '—'}
        </span>
    );

    const joinedBody = (row) => {
        const formatted = formatDate(row.created_at);
        return formatted
            ? <span className="text-sm text-700">{formatted}</span>
            : <span className="text-sm text-500">—</span>;
    };

    const lastLoginBody = (row) => {
        if (!row.last_login_at) return <span className="text-sm text-500">Never</span>;
        const d = new Date(row.last_login_at);
        return (
            <div className="flex flex-column">
                <span className="text-sm text-700">
                    {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <small className="text-500">
                    {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </small>
            </div>
        );
    };

    const leadsBody = (row) => (
        <Tag
            severity={row.total_leads > 0 ? 'info' : 'secondary'}
            value={row.total_leads || 0}
            icon="pi pi-share-alt"
            className="text-xs"
        />
    );

    const statusBody = (row) => (
        <Tag
            severity={row.is_active ? 'success' : 'danger'}
            value={row.is_active ? 'Active' : 'Disabled'}
            icon={row.is_active ? 'pi pi-check' : 'pi pi-ban'}
            className="text-xs"
        />
    );

    const actionsBody = (row) => (
        <div className="flex flex-wrap gap-1 justify-content-center">
            <Button
                icon="pi pi-eye"
                severity="info"
                outlined
                size="small"
                aria-label={`View ${row.name}`}
                tooltip="View agent"
                tooltipOptions={{ position: 'top' }}
                onClick={() => openView(row)}
            />
            <Button
                icon="pi pi-pencil"
                severity="warning"
                outlined
                size="small"
                aria-label={`Edit ${row.name}`}
                tooltip="Edit agent"
                tooltipOptions={{ position: 'top' }}
                onClick={() => openEdit(row)}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-right" />

            <div className="grid">
                <div className="col-12">
                    {/* Page header — stacks on mobile, side-by-side from md */}
                    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-900 m-0 mb-1">Company Agents</h1>
                            <p className="text-600 text-sm m-0">
                                View and manage external agents who refer leads to your company.
                            </p>
                        </div>
                        <div className="flex flex-wrap align-items-center gap-2">
                            <Tag
                                value="Company Admin View"
                                severity="info"
                                icon="pi pi-shield"
                            />
                            {companyName && (
                                <Tag
                                    value={`Company: ${companyName}`}
                                    severity="secondary"
                                    icon="pi pi-building"
                                />
                            )}
                        </div>
                    </div>

                    <Card>
                        {/* Filter bar — 5-slot grid: 2 filters + 2 spacers + action block (P7) */}
                        <div className="surface-ground p-3 border-round mb-3">
                            <div className="grid">
                                <div className="col-12 md:col">
                                    <IconField iconPosition="left" className="w-full">
                                        <InputIcon className="pi pi-search" />
                                        <InputText
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search by name, email, mobile..."
                                            aria-label="Search agents"
                                            className="w-full p-inputtext-sm"
                                        />
                                    </IconField>
                                </div>
                                <div className="col-6 md:col">
                                    <Dropdown
                                        value={statusFilter}
                                        options={STATUS_OPTIONS}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => { setStatusFilter(e.value); setFirst(0); }}
                                        placeholder="All statuses"
                                        showClear
                                        className="w-full p-inputtext-sm"
                                    />
                                </div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="hidden md:block md:col" aria-hidden="true"></div>
                                <div className="col-12 md:col flex gap-2">
                                    <Button
                                        label="Clear"
                                        icon="pi pi-filter-slash"
                                        severity="secondary"
                                        outlined
                                        size="small"
                                        onClick={clearFilters}
                                        aria-label="Clear filters"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                    <Button
                                        label="Refresh"
                                        icon="pi pi-refresh"
                                        outlined
                                        size="small"
                                        onClick={loadAgents}
                                        tooltip="Refresh agent list"
                                        tooltipOptions={{ position: 'top' }}
                                        aria-label="Refresh agent list"
                                        className="flex-1 py-1 md:py-2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Agents table */}
                        <DataTable
                            value={agents}
                            dataKey="id"
                            lazy
                            paginator
                            first={first}
                            rows={rows}
                            totalRecords={totalRecords}
                            onPage={onPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                            currentPageReportTemplate="Showing {first}-{last} of {totalRecords}"
                            paginatorClassName="text-sm"
                            pageLinkSize={3}
                            loading={loading}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={onSort}
                            className="p-datatable-gridlines"
                            responsiveLayout="scroll"
                            scrollable
                            scrollHeight="flex"
                            stripedRows
                            size="small"
                            showGridlines
                            emptyMessage={
                                <div className="flex flex-column align-items-center py-5">
                                    <i className="pi pi-users pi-2x text-400 mb-3" aria-hidden="true" />
                                    <span className="text-600 text-sm">No agents found.</span>
                                    <small className="text-500 mt-1">
                                        Agents are users in your company who refer leads but aren&apos;t employees.
                                    </small>
                                </div>
                            }
                        >
                            <Column
                                field="name"
                                header="Name"
                                body={nameBody}
                                sortable
                                frozen
                                alignFrozen="left"
                                style={{ minWidth: '200px' }}
                            />
                            <Column
                                field="email"
                                header="Email"
                                body={emailBody}
                                sortable
                                style={{ minWidth: '220px' }}
                            />
                            <Column
                                field="mobile_number"
                                header="Mobile"
                                body={mobileBody}
                                style={{ minWidth: '140px' }}
                            />
                            <Column
                                field="created_at"
                                header="Joined"
                                body={joinedBody}
                                sortable
                                style={{ minWidth: '130px' }}
                            />
                            <Column
                                field="last_login_at"
                                header="Last Login"
                                body={lastLoginBody}
                                sortable
                                style={{ minWidth: '140px' }}
                            />
                            <Column
                                field="total_leads"
                                header="Leads Sourced"
                                body={leadsBody}
                                style={{ minWidth: '140px' }}
                            />
                            <Column
                                field="is_active"
                                header="Status"
                                body={statusBody}
                                style={{ minWidth: '120px' }}
                            />
                            <Column
                                header="Actions"
                                body={actionsBody}
                                exportable={false}
                                style={{ minWidth: '120px' }}
                                alignHeader="center"
                                bodyClassName="text-center"
                            />
                        </DataTable>
                    </Card>
                </div>
            </div>

            {/* ── View dialog ───────────────────────────────────────────── */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user text-primary" aria-hidden="true" />
                        <span>Agent Details</span>
                    </div>
                }
                visible={viewDialogVisible}
                onHide={() => setViewDialogVisible(false)}
                modal
                style={{ width: '90vw', maxWidth: '500px' }}
                draggable={false}
                resizable={false}
            >
                {viewLoading ? (
                    <div className="flex flex-column gap-3 py-3">
                        <Skeleton height="2.5rem" />
                        <Skeleton height="2.5rem" />
                        <Skeleton height="2.5rem" />
                    </div>
                ) : viewAgent ? (
                    <div className="flex flex-column gap-3">
                        <DetailRow label="Name"        value={viewAgent.name} />
                        <DetailRow label="First name"  value={viewAgent.first_name || '—'} />
                        <DetailRow label="Last name"   value={viewAgent.last_name || '—'} />
                        <DetailRow label="Email"       value={viewAgent.email || '—'} />
                        <DetailRow label="Mobile"      value={viewAgent.mobile_number || '—'} />
                        <DetailRow
                            label="Status"
                            value={
                                <Tag
                                    severity={viewAgent.is_active ? 'success' : 'danger'}
                                    value={viewAgent.is_active ? 'Active' : 'Disabled'}
                                    icon={viewAgent.is_active ? 'pi pi-check' : 'pi pi-ban'}
                                    className="text-xs"
                                />
                            }
                        />
                        <DetailRow
                            label="Leads sourced"
                            value={
                                <Tag
                                    severity={viewAgent.total_leads > 0 ? 'info' : 'secondary'}
                                    value={viewAgent.total_leads ?? 0}
                                    icon="pi pi-share-alt"
                                    className="text-xs"
                                />
                            }
                        />
                        <DetailRow label="Joined"     value={formatDateTime(viewAgent.created_at) || '—'} />
                        <DetailRow label="Last login" value={formatDateTime(viewAgent.last_login_at) || 'Never'} />
                    </div>
                ) : (
                    <Message severity="warn" text="Could not load agent details." />
                )}
            </Dialog>

            {/* ── Edit dialog ───────────────────────────────────────────── */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-pencil text-orange-500" aria-hidden="true" />
                        <span>Edit Agent</span>
                    </div>
                }
                visible={editDialogVisible}
                onHide={savingEdit ? () => {} : () => setEditDialogVisible(false)}
                modal
                style={{ width: '90vw', maxWidth: '520px' }}
                draggable={false}
                resizable={false}
                closable={!savingEdit}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            outlined
                            size="small"
                            onClick={() => setEditDialogVisible(false)}
                            disabled={savingEdit}
                        />
                        <Button
                            label={savingEdit ? 'Saving…' : 'Save Changes'}
                            icon="pi pi-check"
                            severity="success"
                            size="small"
                            loading={savingEdit}
                            onClick={submitEdit}
                        />
                    </div>
                }
            >
                <div className="grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="first_name" className="block font-medium text-sm mb-2">
                            First name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="first_name"
                            value={editForm.first_name}
                            onChange={(e) => setEditForm((p) => ({ ...p, first_name: e.target.value }))}
                            maxLength={100}
                            autoComplete="given-name"
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': editErrors.first_name })}
                            aria-describedby="first_name-help"
                            aria-invalid={!!editErrors.first_name}
                        />
                        {editErrors.first_name && (
                            <small id="first_name-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {editErrors.first_name}
                            </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="last_name" className="block font-medium text-sm mb-2">
                            Last name <span className="text-red-500">*</span>
                        </label>
                        <InputText
                            id="last_name"
                            value={editForm.last_name}
                            onChange={(e) => setEditForm((p) => ({ ...p, last_name: e.target.value }))}
                            maxLength={100}
                            autoComplete="family-name"
                            className={classNames('w-full p-inputtext-sm', { 'p-invalid': editErrors.last_name })}
                            aria-describedby="last_name-help"
                            aria-invalid={!!editErrors.last_name}
                        />
                        {editErrors.last_name && (
                            <small id="last_name-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {editErrors.last_name}
                            </small>
                        )}
                    </div>

                    <div className="field col-12">
                        <label htmlFor="email" className="block font-medium text-sm mb-2">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-at" />
                            <InputText
                                id="email"
                                type="email"
                                inputMode="email"
                                autoCapitalize="none"
                                spellCheck={false}
                                autoComplete="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))}
                                maxLength={255}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': editErrors.email })}
                                aria-describedby="email-help"
                                aria-invalid={!!editErrors.email}
                            />
                        </IconField>
                        {editErrors.email && (
                            <small id="email-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {editErrors.email}
                            </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="mobile_number" className="block font-medium text-sm mb-2">
                            Mobile <span className="text-red-500">*</span>
                        </label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-phone" />
                            <InputText
                                id="mobile_number"
                                inputMode="tel"
                                autoComplete="tel-national"
                                value={editForm.mobile_number}
                                onChange={(e) => setEditForm((p) => ({
                                    ...p,
                                    mobile_number: e.target.value.replace(/\D/g, '').slice(0, 10),
                                }))}
                                maxLength={10}
                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': editErrors.mobile_number })}
                                aria-describedby="mobile_number-help"
                                aria-invalid={!!editErrors.mobile_number}
                            />
                        </IconField>
                        {editErrors.mobile_number && (
                            <small id="mobile_number-help" className="p-error block mt-1" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                {editErrors.mobile_number}
                            </small>
                        )}
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="is_active" className="block font-medium text-sm mb-2">
                            Status
                        </label>
                        <Dropdown
                            id="is_active"
                            value={editForm.is_active}
                            options={ACTIVE_OPTIONS}
                            optionLabel="label"
                            optionValue="value"
                            onChange={(e) => setEditForm((p) => ({ ...p, is_active: e.value }))}
                            className="w-full p-inputtext-sm"
                        />
                    </div>
                </div>

                <Divider className="my-2" />
                <Message
                    severity="info"
                    text="Impersonation is not available for agents. To act on their behalf, use the lead-sharing or assignment workflows."
                    className="w-full"
                />
            </Dialog>
        </>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="flex flex-column md:flex-row md:align-items-center gap-1 md:gap-3">
        <span className="text-600 text-sm w-8rem">{label}</span>
        <span className="text-900 text-sm font-medium">{value}</span>
    </div>
);

export default CompanyAgentManagement;
